import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, Tag, Share2, Clock } from 'lucide-react';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/blogs/slug/${slug}`);
        setBlog(response.data);
      } catch (error) {
        console.error('Failed to fetch blog:', error);
        if (error.response?.status === 404) {
          navigate('/blog');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug, navigate]);

  // Extract headings for Table of Contents
  const tableOfContents = useMemo(() => {
    if (!blog?.content) return [];
    
    // First decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = blog.content;
    const decodedContent = textarea.value;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(decodedContent, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3');
    
    return Array.from(headings).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
      level: parseInt(heading.tagName.charAt(1))
    }));
  }, [blog?.content]);

  // Add IDs to headings in content
  const processedContent = useMemo(() => {
    if (!blog?.content) return '';
    
    // First decode HTML entities (handles &lt; &gt; etc.)
    const textarea = document.createElement('textarea');
    textarea.innerHTML = blog.content;
    let decodedContent = textarea.value;
    
    // If still contains escaped entities, decode again
    if (decodedContent.includes('&lt;') || decodedContent.includes('&gt;')) {
      textarea.innerHTML = decodedContent;
      decodedContent = textarea.value;
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(decodedContent, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3');
    
    headings.forEach((heading, index) => {
      heading.id = `heading-${index}`;
    });
    
    return doc.body.innerHTML;
  }, [blog?.content]);

  // Scroll spy for Table of Contents
  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('.blog-content h1, .blog-content h2, .blog-content h3');
      let currentActive = '';
      
      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          currentActive = heading.id;
        }
      });
      
      setActiveSection(currentActive);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    if (!content) return '1 min';
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog?.title,
          text: blog?.excerpt || blog?.seo?.meta_description,
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
          <div className="max-w-4xl mx-auto">
            <div className="skeleton h-8 w-32 mb-8 rounded"></div>
            <div className="skeleton h-12 w-3/4 mb-4 rounded"></div>
            <div className="skeleton h-6 w-1/2 mb-8 rounded"></div>
            <div className="skeleton h-96 w-full mb-8 rounded-2xl"></div>
            <div className="space-y-4">
              <div className="skeleton h-4 w-full rounded"></div>
              <div className="skeleton h-4 w-full rounded"></div>
              <div className="skeleton h-4 w-3/4 rounded"></div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!blog) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 text-center">
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-4">
            Blog post not found
          </h1>
          <Link to="/blog" className="text-[#6F42F0] hover:underline">
            Back to Blog
          </Link>
        </div>
      </PublicLayout>
    );
  }

  // SEO meta tags - ensure all values are strings
  const seoTitle = String(blog.seo?.meta_title || blog.title || 'Blog Post');
  const seoDescription = String(blog.seo?.meta_description || blog.excerpt || '');
  const seoKeywords = String(blog.seo?.meta_keywords || '');
  const ogTitle = String(blog.seo?.og_title || blog.title || 'Blog Post');
  const ogDescription = String(blog.seo?.og_description || blog.excerpt || '');
  const ogImage = blog.seo?.og_image || blog.featured_image || '';
  const canonicalUrl = blog.seo?.canonical_url || window.location.href;

  return (
    <PublicLayout>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`${seoTitle} | WingsDiet Blog`}</title>
        <meta name="description" content={seoDescription || ''} />
        {seoKeywords ? <meta name="keywords" content={seoKeywords} /> : null}
        <meta property="og:title" content={ogTitle || ''} />
        <meta property="og:description" content={ogDescription || ''} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonicalUrl || ''} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle || ''} />
        <meta name="twitter:description" content={ogDescription || ''} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient py-8 md:py-12" data-testid="blog-detail-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Breadcrumb */}
          <nav className="mb-6 animate-fade-in">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/blog" className="text-slate-500 hover:text-[#6F42F0] transition-colors flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" />
                  Back to Blog
                </Link>
              </li>
            </ol>
          </nav>

          {/* Header Content */}
          <div className="max-w-4xl animate-slide-up">
            {/* Category & Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {blog.category_name && (
                <Link to={`/category/${blog.category_id}`}>
                  <Badge variant="secondary" className="category-badge">
                    <Tag className="w-3 h-3 mr-1" />
                    {blog.category_name}
                  </Badge>
                </Link>
              )}
              <span className="text-slate-500 text-sm flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(blog.published_at)}
              </span>
              <span className="text-slate-500 text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {calculateReadTime(blog.content)}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-['Manrope'] text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              {blog.title}
            </h1>

            {/* Excerpt */}
            {blog.excerpt && (
              <p className="text-slate-600 text-lg md:text-xl leading-relaxed mb-6">
                {blog.excerpt}
              </p>
            )}

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-[#6F42F0] hover:text-[#6F42F0] transition-all text-sm font-medium"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {blog.featured_image && (
        <section className="container mx-auto px-4 md:px-8 max-w-7xl mt-8 mb-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <img
                src={blog.featured_image.startsWith('/api') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${blog.featured_image}` 
                  : blog.featured_image}
                alt={blog.title}
                className="w-full h-auto"
                data-testid="featured-image"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content Section */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl pb-16 md:pb-24" data-testid="blog-content-section">
        <div className="flex gap-12">
          {/* Main Content */}
          <article className="flex-1 max-w-4xl mx-auto lg:mx-0">
            <div 
              className="blog-content prose prose-lg max-w-none animate-fade-in"
              dangerouslySetInnerHTML={{ __html: processedContent }}
              data-testid="blog-content"
            />
          </article>

          {/* Table of Contents - Desktop Only */}
          {tableOfContents.length > 0 && (
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky-toc p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <h4 className="font-['Manrope'] font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wider">
                  Table of Contents
                </h4>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToHeading(item.id)}
                      className={`block text-left text-sm transition-colors w-full truncate ${
                        activeSection === item.id 
                          ? 'text-[#6F42F0] font-medium' 
                          : 'text-slate-500 hover:text-slate-900'
                      }`}
                      style={{ paddingLeft: `${(item.level - 1) * 12}px` }}
                      data-testid={`toc-${item.id}`}
                    >
                      {item.text}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}
        </div>
      </section>

      {/* App Download CTA */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">

            {/* Left: Logo + Text */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-5">
                <img src="/logo.png" alt="WingsDiet" className="h-12 w-auto" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-3 leading-tight">
                Eat Smart,<br className="hidden md:block" /> Live Better
              </h2>
              <p className="text-slate-500 text-base mb-7 max-w-sm mx-auto md:mx-0">
                Track meals, get personalized diet plans, and hit your health goals — all in one place. Free to download.
              </p>

              {/* Store Badges */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <a
                  href="https://play.google.com/store/apps/details?id=com.wingsdiet.nutrition"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 bg-[#1a1a1a] text-white px-5 py-3 rounded-xl hover:bg-[#333] transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
                    <path d="M7.1 4.4L27.7 24 7.1 43.6a3 3 0 01-1.1-2.3V6.7a3 3 0 011.1-2.3z" fill="#EA4335"/>
                    <path d="M34.7 17.3L28 20.9 7.1 4.4a3 3 0 011.7-.4l25.9 13.3z" fill="#FBBC04"/>
                    <path d="M34.7 30.7L8.8 44a3 3 0 01-1.7-.4L27.7 24l7 6.7z" fill="#34A853"/>
                    <path d="M44 24c0 1.3-.7 2.5-1.9 3.1l-7.4 3.6L28 24l6.7-6.7 7.4 3.6A3.5 3.5 0 0144 24z" fill="#4285F4"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-white/60 leading-none uppercase tracking-wide">Get it on</div>
                    <div className="text-base font-semibold leading-tight">Google Play</div>
                  </div>
                </a>

                <a
                  href="https://apps.apple.com/us/app/wingsdiet/id6748774685"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 bg-[#1a1a1a] text-white px-5 py-3 rounded-xl hover:bg-[#333] transition-colors"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-white/60 leading-none uppercase tracking-wide">Download on the</div>
                    <div className="text-base font-semibold leading-tight">App Store</div>
                  </div>
                </a>
              </div>

              {/* Rating */}
              <div className="mt-6 flex items-center gap-2 justify-center md:justify-start">
                <div className="flex text-yellow-400 text-sm">★★★★★</div>
                <span className="text-slate-500 text-sm">4.8 &nbsp;·&nbsp; 204 Reviews</span>
              </div>
            </div>

            {/* Right: Feature pills */}
            <div className="flex-1 w-full max-w-sm mx-auto md:mx-0">
              <div className="bg-slate-50 rounded-3xl p-7 space-y-4">
                {[
                  { icon: '🥗', title: 'Smart Meal Tracking', desc: 'Log meals instantly with AI-powered food recognition' },
                  { icon: '📊', title: 'Nutrition Analytics', desc: 'Real-time macros: carbs, protein, fat & calories' },
                  { icon: '🎯', title: 'Personalized Plans', desc: 'Diet plans tailored to your body and goals' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-4 bg-white rounded-2xl p-4 shadow-sm">
                    <span className="text-2xl">{f.icon}</span>
                    <div>
                      <div className="font-semibold text-[#1a2332] text-sm">{f.title}</div>
                      <div className="text-slate-500 text-xs mt-0.5 leading-relaxed">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default BlogDetail;
